import { inheritRequestCorrelation } from '@brighthub/structured-logging';
import { Injectable, inject } from '@angular/core';
import { ApiService, GenericResponseCode } from '@brighthub/api';
import { Observable, map } from 'rxjs';
import { SystemCodeCatalogService } from '../../system-codes/system-code-catalog.service';
import {
  CreateOutcomeRequest,
  LinkOutcomeRequest,
  OutcomeCard,
  OutcomeListPayload,
  OutcomeType,
  SaveOutcomeRequest,
} from './outcome-api.model';
import { OutcomeApi } from './outcome-api.token';

interface OutcomeEnvelope<T> {
  result: { status: number; code: string; message: string };
  payload: T;
}

const PRIVATE_WRITE = { suppressRequestBodyTelemetry: true };

@Injectable()
export class HttpOutcomeApiService implements OutcomeApi {
  private readonly api = inject(ApiService);
  private readonly catalog = inject(SystemCodeCatalogService);

  list(caseId: number): Observable<OutcomeListPayload> {
    return this.api
      .get<
        OutcomeEnvelope<OutcomeListPayload>
      >('api-internal', `/outcome?caseId=${this.id(caseId)}`)
      .pipe(map((response) => this.payload(response)));
  }

  listForClient(clientId: number): Observable<OutcomeListPayload> {
    return this.api
      .get<
        OutcomeEnvelope<OutcomeListPayload>
      >('api-internal', `/outcome?clientId=${this.id(clientId)}`)
      .pipe(map((response) => this.payload(response)));
  }

  get(caseId: number, outcomeId: number): Observable<OutcomeCard> {
    return this.api
      .get<
        OutcomeEnvelope<OutcomeCard>
      >('api-internal', `/outcome/${this.id(outcomeId)}?caseId=${this.id(caseId)}`)
      .pipe(map((response) => this.payload(response)));
  }

  types(refresh = false): Observable<readonly OutcomeType[]> {
    const catalog$ = refresh ? this.catalog.reload() : this.catalog.load();
    return catalog$.pipe(
      map((catalog) => this.catalog.values(catalog, 'outcome_type')),
    );
  }

  create(body: CreateOutcomeRequest): Observable<OutcomeCard> {
    return this.api
      .post<OutcomeEnvelope<OutcomeCard>>(
        'api-internal',
        '/outcome',
        {
          ...this.fields(body),
          clientId: this.id(body.clientId),
        },
        PRIVATE_WRITE,
      )
      .pipe(map((response) => this.payload(response)));
  }

  update(outcomeId: number, body: SaveOutcomeRequest): Observable<OutcomeCard> {
    return this.api
      .patch<
        OutcomeEnvelope<OutcomeCard>
      >('api-internal', `/outcome/${this.id(outcomeId)}`, this.fields(body), PRIVATE_WRITE)
      .pipe(map((response) => this.payload(response)));
  }

  invalidate(outcomeId: number, reason: string): Observable<OutcomeCard> {
    return this.api
      .post<OutcomeEnvelope<OutcomeCard>>(
        'api-internal',
        `/outcome/${this.id(outcomeId)}/invalidate`,
        {
          invalidatedReason: reason.trim(),
        },
        PRIVATE_WRITE,
      )
      .pipe(map((response) => this.payload(response)));
  }

  link(outcomeId: number, body: LinkOutcomeRequest): Observable<OutcomeCard> {
    return this.api
      .post<
        OutcomeEnvelope<OutcomeCard>
      >('api-internal', `/outcome/${this.id(outcomeId)}/link`, { caseId: this.id(body.caseId), linkedOptions: body.linkedOptions.map((id) => this.id(id)) }, PRIVATE_WRITE)
      .pipe(map((response) => this.payload(response)));
  }

  private fields(body: SaveOutcomeRequest) {
    return {
      ...(body.caseId !== undefined ? { caseId: this.id(body.caseId) } : {}),
      name: body.name.trim(),
      ...(body.description !== undefined
        ? { description: body.description?.trim() || '' }
        : {}),
      outcomeTypeCode: body.outcomeTypeCode,
      // Without a case, PATCH edits content only; no link fields cross the API.
      ...(body.caseId !== undefined
        ? { linkedOptions: body.linkedOptions.map((id) => this.id(id)) }
        : {}),
      ...(body.atiValue !== undefined ? { atiValue: body.atiValue } : {}),
    };
  }

  unlink(outcomeId: number, body: LinkOutcomeRequest): Observable<OutcomeCard> {
    return this.api
      .post<
        OutcomeEnvelope<OutcomeCard>
      >('api-internal', `/outcome/${this.id(outcomeId)}/unlink`, { caseId: this.id(body.caseId), linkedOptions: body.linkedOptions.map((id) => this.id(id)) }, PRIVATE_WRITE)
      .pipe(map((response) => this.payload(response)));
  }

  private id(id: number): number {
    if (!Number.isSafeInteger(id) || id <= 0)
      throw new Error(
        'A valid outcome, case, or option identifier is required.',
      );
    return id;
  }

  private payload<T>(response: OutcomeEnvelope<T>): T {
    if (
      response?.result?.code !== GenericResponseCode.SERVICE_OK ||
      response.payload == null
    ) {
      throw inheritRequestCorrelation(
        Object.assign(
          new Error(
            response?.result?.message || 'Outcomes could not be loaded.',
          ),
          { status: response?.result?.status },
        ),
        response,
      );
    }
    return response.payload;
  }
}
